import { Account, PermissionSet, PermissionSetAssignment, PortalUserData } from '../../../types';

export const setName = (permissionSets: PermissionSet[], id: string): string =>
  permissionSets.find((set) => set.id === id)?.name ?? 'Assigned';

export const userDisplayName = (user: PortalUserData): string => {
  const name = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();
  return name || user.email;
};

export const userAccounts = (user: PortalUserData): Account[] => {
  if (user.accounts?.length) return user.accounts;
  if (user.account_id) return [{ id: user.account_id, name: user.account_name ?? user.account_id }];
  return [];
};

export const userAssignmentAccounts = (userData: PortalUserData, assignments: PermissionSetAssignment[]): Account[] => {
  const known = userAccounts(userData);
  const knownIds = new Set(known.map((account) => account.id));
  const extraIds = assignments
    .filter(
      (assignment) =>
        assignment.team_id == null &&
        assignment.account_id != null &&
        String(assignment.portal_user_id) === String(userData.id) &&
        !knownIds.has(assignment.account_id)
    )
    .map((assignment) => assignment.account_id as string);
  const extra = [...new Set(extraIds)].map((id) => ({ id, name: id }));
  return [...known, ...extra];
};

export const assignmentForUser = (
  assignments: PermissionSetAssignment[],
  accountId: string,
  userId: string
): PermissionSetAssignment | null =>
  assignments.find(
    (assignment) =>
      assignment.team_id == null &&
      assignment.account_id === accountId &&
      String(assignment.portal_user_id) === String(userId)
  ) ?? null;

export const assignmentForTeam = (
  assignments: PermissionSetAssignment[],
  teamId: string
): PermissionSetAssignment | null =>
  assignments.find((assignment) => assignment.team_id != null && String(assignment.team_id) === teamId) ?? null;

export const usersOnSet = (
  roster: PortalUserData[],
  assignments: PermissionSetAssignment[],
  accountId: string,
  permissionSetId: string
): PortalUserData[] =>
  roster.filter((user) => assignmentForUser(assignments, accountId, user.id)?.permission_set_id === permissionSetId);

export const teamsOnSet = <T extends { id: string }>(
  teams: T[],
  assignments: PermissionSetAssignment[],
  permissionSetId: string
): T[] => teams.filter((team) => assignmentForTeam(assignments, team.id)?.permission_set_id === permissionSetId);
